import Vue from 'vue';
import Push from 'push.js';
import Api from './Api.js';
import SystemSocket from './SystemSocket.js';
import SystemUsersManager from './SystemUsersManager.js';
import { SOUNDS } from './SystemNotifications.js';

// Globalny menedżer chatu firmowego. Stan reaktywny (Vue.observable) czytany przez
// ChatPanel i badge w nagłówku; wiadomości przychodzą przez istniejący socket push-service
// (event 'object' z object == 'chat_message' / 'chat_room').

const SOUND_STORAGE_KEY = 'frontend_notification_sound';
const DEFAULT_SOUND_KEY = 'bell';
const PAGE_SIZE = 50;

const state = Vue.observable({
    isPanelOpen: false,
    activeRoomId: null,
    rooms: [],
    messagesByRoom: {},
    hasMoreByRoom: {},
    isLoaded: false,
    isLoadingMessages: false,
});

let myUserId = null;
let isInitialized = false;
let soundKey = DEFAULT_SOUND_KEY;

function findRoom(roomId) {
    return state.rooms.find(r => String(r._id) == String(roomId));
}

function sortRooms() {
    state.rooms.sort((a, b) => {
        if (a.type == 'general') return -1;
        if (b.type == 'general') return 1;
        return new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt);
    });
}

function totalUnread() {
    return state.rooms.reduce((sum, r) => sum + (r.unreadCount || 0), 0);
}

// Nazwa pokoju do wyświetlenia: dla DM imię drugiej osoby
function getRoomDisplayName(room) {
    if (!room) return '';
    if (room.type == 'dm') {
        const other = (room.members || []).find(m => String(m.userId) != String(myUserId));
        return other ? SystemUsersManager.getName(other.userId) : 'Rozmowa';
    }
    if (room.type == 'general') return '#general';
    return room.name || 'Pokój';
}

function playSound() {
    if (!soundKey || !SOUNDS[soundKey]) return;
    const audio = new Audio(SOUNDS[soundKey]);
    audio.play();
}

// Tekst z tokenami @[u:<id>] / #[o:<id>|<nazwa>] zamieniony na czytelny (do podglądów i dymków)
function renderPlainText(text) {
    return String(text || '')
        .replace(/@\[u:([0-9a-fA-F]{24})\]/g, (m, id) => '@' + SystemUsersManager.getName(id))
        .replace(/#\[o:[0-9a-fA-F]{24}\|([^\]\n]*)\]/g, (m, name) => '#' + name);
}

async function refreshRooms() {
    const rooms = await Api.chat.getRooms();
    state.rooms = rooms;
    sortRooms();
    state.isLoaded = true;
}

function handleIncomingMessage(payload) {
    const message = payload.instance;
    if (!message) return;
    const roomId = String(message.roomId);
    let room = findRoom(roomId);

    if (!room) {
        // pokój nieznany (np. dołączony przed chwilą / wyścig) — dociągnij listę
        refreshRooms().catch(() => {});
        room = null;
    } else {
        room.lastMessage = { text: message.text, authorId: message.authorId, authorName: message.authorName, createdAt: message.createdAt };
        room.lastMessageAt = message.createdAt;
        sortRooms();
    }

    const loaded = state.messagesByRoom[roomId];
    if (loaded && !loaded.some(m => String(m._id) == String(message._id))) {
        loaded.push(message);
    }

    if (String(message.authorId) == String(myUserId)) return;

    const isActiveVisible = state.isPanelOpen
        && String(state.activeRoomId) == roomId
        && !document.hidden;

    if (isActiveVisible) {
        markRead(roomId).catch(() => {});
        return;
    }

    if (room) room.unreadCount = (room.unreadCount || 0) + 1;

    const isMentioned = (message.mentions || []).some(id => String(id) == String(myUserId));
    const roomName = payload.room ? (payload.room.type == 'dm' ? message.authorName : (payload.room.type == 'general' ? '#general' : payload.room.name)) : '';
    const title = (isMentioned ? 'Wspomniano Cię: ' : '') + (message.authorName || 'Nowa wiadomość') + (roomName && payload.room.type != 'dm' ? ' w ' + roomName : '');

    playSound();
    Push.create(title, {
        body: renderPlainText(message.text).substring(0, 180),
        timeout: 5000,
        onClick: function () {
            window.focus();
            openRoom(roomId);
            this.close();
        }
    });
}

function handleRoomEvent(verb, payload) {
    const room = payload.instance;
    if (!room) return;

    if (payload.event == 'member_removed' && String(payload.removedUserId) == String(myUserId)) {
        const idx = state.rooms.findIndex(r => String(r._id) == String(room._id));
        if (idx != -1) state.rooms.splice(idx, 1);
        Vue.delete(state.messagesByRoom, String(room._id));
        if (String(state.activeRoomId) == String(room._id)) state.activeRoomId = null;
        return;
    }

    const existing = findRoom(room._id);
    if (existing) {
        existing.name = room.name;
        existing.members = room.members;
    } else {
        state.rooms.push(Object.assign({ unreadCount: 0 }, room));
        sortRooms();
    }
}

function handleReadEvent(payload) {
    // sync badge między kartami tego samego usera
    if (String(payload.userId) != String(myUserId)) return;
    const room = findRoom(payload.roomId);
    if (room) {
        room.unreadCount = 0;
        room.myLastReadAt = payload.lastReadAt;
    }
}

function init() {
    if (isInitialized) return;
    isInitialized = true;

    myUserId = null;
    // UserManager importuje SystemSocket itd.; unikamy cyklu importów pobierając leniwie
    const UserManager = require('./UserManager.js').default;
    myUserId = UserManager.user && UserManager.user.userId;

    SystemUsersManager.ensureLoaded();
    refreshRooms().catch(() => {});

    Api.system.storage.get(SOUND_STORAGE_KEY)
        .then(saved => { if (saved !== undefined && saved !== null) soundKey = saved.value; })
        .catch(() => {});

    SystemSocket.socket.on('object', (msg) => {
        if (!msg || !msg.data) return;
        if (msg.object == 'chat_message' && msg.verb == 'create') handleIncomingMessage(msg.data);
        else if (msg.object == 'chat_room' && (msg.verb == 'create' || msg.verb == 'update')) handleRoomEvent(msg.verb, msg.data);
        else if (msg.object == 'chat_room' && msg.verb == 'read') handleReadEvent(msg.data);
    });

    // po reconnect mogliśmy zgubić eventy — wylecz stan
    SystemSocket.socket.on('reconnect', () => {
        refreshRooms().catch(() => {});
        if (state.activeRoomId) reloadRoomTail(state.activeRoomId).catch(() => {});
    });
}

function togglePanel() {
    state.isPanelOpen = !state.isPanelOpen;
    if (!state.isPanelOpen) state.activeRoomId = null;
    else if (!state.isLoaded) refreshRooms().catch(() => {});
}

function closePanel() {
    state.isPanelOpen = false;
    state.activeRoomId = null;
}

async function openRoom(roomId) {
    state.isPanelOpen = true;
    state.activeRoomId = String(roomId);
    if (!state.messagesByRoom[String(roomId)]) {
        await loadMessages(roomId);
    }
    await markRead(roomId);
}

function closeRoom() {
    state.activeRoomId = null;
}

async function openDm(userId) {
    const room = await Api.chat.openDm(userId);
    if (!findRoom(room._id)) {
        state.rooms.push(Object.assign({ unreadCount: 0 }, room));
        sortRooms();
    }
    await openRoom(room._id);
    return room;
}

async function loadMessages(roomId) {
    state.isLoadingMessages = true;
    try {
        const messages = await Api.chat.getMessages(roomId, { limit: PAGE_SIZE });
        messages.reverse();
        Vue.set(state.messagesByRoom, String(roomId), messages);
        Vue.set(state.hasMoreByRoom, String(roomId), messages.length >= PAGE_SIZE);
    } finally {
        state.isLoadingMessages = false;
    }
}

// Dociąga najnowszą stronę i scala z tym co mamy (po reconnect)
async function reloadRoomTail(roomId) {
    const messages = await Api.chat.getMessages(roomId, { limit: PAGE_SIZE });
    messages.reverse();
    const loaded = state.messagesByRoom[String(roomId)] || [];
    const known = new Set(loaded.map(m => String(m._id)));
    messages.forEach(m => { if (!known.has(String(m._id))) loaded.push(m); });
    loaded.sort((a, b) => String(a._id).localeCompare(String(b._id)));
    Vue.set(state.messagesByRoom, String(roomId), loaded);
}

async function loadOlder(roomId) {
    const loaded = state.messagesByRoom[String(roomId)];
    if (!loaded || !loaded.length || !state.hasMoreByRoom[String(roomId)]) return 0;
    const older = await Api.chat.getMessages(roomId, { before: loaded[0]._id, limit: PAGE_SIZE });
    older.reverse();
    state.messagesByRoom[String(roomId)] = older.concat(loaded);
    Vue.set(state.hasMoreByRoom, String(roomId), older.length >= PAGE_SIZE);
    return older.length;
}

async function send(roomId, text, mentions) {
    const message = await Api.chat.sendMessage(roomId, { text, mentions: mentions || [] });
    const loaded = state.messagesByRoom[String(roomId)];
    if (loaded && !loaded.some(m => String(m._id) == String(message._id))) loaded.push(message);
    const room = findRoom(roomId);
    if (room) {
        room.lastMessage = { text: message.text, authorId: message.authorId, authorName: message.authorName, createdAt: message.createdAt };
        room.lastMessageAt = message.createdAt;
        sortRooms();
    }
    return message;
}

async function markRead(roomId) {
    const room = findRoom(roomId);
    if (room) room.unreadCount = 0;
    await Api.chat.markRead(roomId);
}

async function createRoom(name, memberIds) {
    const room = await Api.chat.createRoom({ name, memberIds });
    if (!findRoom(room._id)) {
        state.rooms.push(Object.assign({ unreadCount: 0 }, room));
        sortRooms();
    }
    return room;
}

function getMyUserId() {
    return myUserId;
}

export default {
    state,
    init,
    togglePanel,
    closePanel,
    openRoom,
    closeRoom,
    openDm,
    loadMessages,
    loadOlder,
    send,
    markRead,
    createRoom,
    refreshRooms,
    totalUnread,
    getRoomDisplayName,
    renderPlainText,
    getMyUserId,
};
